import { Button } from "@/components/ui/button";
import { Model, PlaygroundParametersProps } from "@/types";
// import { ButtonGroup as ModeSelection } from "../../../components/ui/button-group";
import { ChevronDown, Cpu, Globe, Search } from "lucide-react";
import { createPortal } from "react-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IDefaultModel } from "@/types/conversations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useId, useMemo, useState } from "react";
import * as Slider from "@radix-ui/react-slider";
import { cn } from "@/utils/cn";
import { SliderWithLabel } from "@/components/ui/slider-with-label";
import AgentToolsList from "./agent-tools/agent-tools-list";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import { PlaygroundParametersMobile } from "./playground-parameters-mobile";
import { useModelsQuery } from "@/features/playground/hooks/useModelQueries";
import { usePlaygroundPresentation } from "./playground-presentation";

/* Mode selection is hidden while Agentic is the only supported mode.
const modes = [
  {
    value: "agentic",
    labelKey: "playground.agentic",
    icon: <Sparkles className="h-3 w-3" />,
    tooltipKey: "playground.agenticTooltip",
  },
];
*/
export const PlaygroundParameters = ({
  selectedModel,
  temperature,
  maxTokens,
  onModelChange,
  onTemperatureChange,
  onMaxTokensChange,
  mode,
  onModeChange,
  onPlannerModelChange,
  selectedPlannerModel,
  language,
  onLanguageChange,
  leftActions,
  leadingActions,
  onOpenToolsMenu,
  toolsMenuState,
  defaultModels,
}: PlaygroundParametersProps) => {
  const { t, i18n } = useTranslation();
  const { modern, mobile, settingsOpen, settingsHost, settingsId, openSettings, closeSettings } = usePlaygroundPresentation();
  const [openChange, setOpenChange] = useState(false);
  const modelsQuery = useModelsQuery();
  const models = modelsQuery.data;

  const showPanel = modern && !mobile && settingsOpen && settingsHost;
  if (!modelsQuery.ready || modelsQuery.isPending) {
    const loading = <Skeleton className={modern ? "h-36 motion-reduce:animate-none" : "motion-reduce:animate-none"} />;
    return showPanel ? createPortal(<div role="status" aria-label={t("common.loading")} className="px-3 py-4">{loading}</div>, settingsHost) : loading;
  }
  if (modelsQuery.isError) {
    const error = (
      <p role="alert" className="text-sm text-destructive dark:text-destructive-foreground">
        {t("aiCatalog.failedToLoadModels")}
      </p>
    );
    return showPanel ? createPortal(error, settingsHost) : error;
  }
  const model = models?.find((model) => model.id === selectedModel);
  return (
    <div className="flex flex-col">
      {showPanel && createPortal(!models?.length ? <p role="status" className="px-3 py-4 text-sm text-foreground/70">{t("usageAndCost.llmConfigs.noModelsFound")}</p> : <div className="flex flex-col gap-4 border-b border-border px-3 pb-5 pt-1" onKeyDown={event => {
        // React portal events follow the composer tree rather than the aside.
        if (event.key === "Escape" && !event.defaultPrevented) { event.preventDefault(); event.stopPropagation(); closeSettings(true); }
      }}>
        <div className="flex flex-col gap-1">
          <span id={`${settingsId}-model`} className="text-xs font-medium leading-[18px] text-foreground/70">{t("playground.baseModel")}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={!models?.length}>
              <Button type="button" variant="outline" aria-labelledby={`${settingsId}-model`} className="h-9 w-full justify-between rounded-lg border-border bg-input px-3 font-normal motion-reduce:transition-none">
                <Cpu aria-hidden="true" className="size-5 shrink-0" />
                <span className="min-w-0 flex-1 truncate text-start">{model?.name ?? t("common.model")}</span>
                <ChevronDown aria-hidden="true" className="size-4 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <ModelMenuSub panel type="base" models={models} selectedModel={model} onModelChange={onModelChange} defaultModels={defaultModels} />
          </DropdownMenu>
        </div>
        <RunSettingsSlider label={t("playground.temperature")} min={0} max={1} step={0.01} value={temperature} onChange={onTemperatureChange} />
        <RunSettingsSlider label={t("playground.maximumTokens")} min={0} max={model?.max_tokens || 4096}
          step={Math.max(1, Math.floor((model?.max_tokens || 4096) / 100))} value={maxTokens} onChange={onMaxTokensChange} />
        <div className="flex flex-col gap-1">
          <span id={`${settingsId}-language`} className="text-xs font-medium leading-[18px] text-foreground/70">{t("playground.language")}</span>
          <Select dir={i18n.dir()} value={["auto", "en", "ar"].includes(language) ? language : "auto"} onValueChange={onLanguageChange}>
            <SelectTrigger aria-labelledby={`${settingsId}-language`} className="h-9 w-full rounded-lg border-border bg-input motion-reduce:transition-none">
              <Globe aria-hidden="true" className="size-5" /><SelectValue />
            </SelectTrigger>
            <SelectContent className="motion-reduce:animate-none">
              <SelectItem value="auto">{t("playground.auto")}</SelectItem>
              <SelectItem value="en">{t("playground.english")}</SelectItem>
              <SelectItem value="ar">{t("playground.arabic")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>, settingsHost)}
      <div className={modern ? "min-[900px]:hidden" : "md:hidden"}>
        <PlaygroundParametersMobile
          models={models}
          selectedModel={selectedModel}
          selectedPlannerModel={selectedPlannerModel}
          temperature={temperature}
          maxTokens={maxTokens}
          mode={mode}
          language={language}
          onModelChange={onModelChange}
          onPlannerModelChange={onPlannerModelChange}
          onModeChange={onModeChange}
          onTemperatureChange={onTemperatureChange}
          onMaxTokensChange={onMaxTokensChange}
          onLanguageChange={onLanguageChange}
          leftActions={leftActions}
          leadingActions={leadingActions}
          onOpenToolsMenu={onOpenToolsMenu}
          toolsMenuState={toolsMenuState}
          defaultModels={defaultModels}
        />
      </div>
      <div className={cn("hidden flex-wrap justify-between items-center gap-y-2", modern ? "min-[900px]:flex" : "md:flex")}>
        {leadingActions ? (
          <div className="flex items-center gap-2 min-w-0">
            {leadingActions}
          </div>
        ) : null}
        {/*
        <div className="flex items-center gap-2 min-w-0">
          <ModeSelection
            className="w-fit grid-cols-1"
            value={mode}
            onValueChange={onModeChange}
            options={modes.map((mode) => ({
              ...mode,
              label: t(mode.labelKey),
              icon: mode.icon, // Correctly passing the icon property
              tooltip: t(mode.tooltipKey), // Passing the tooltip property
            }))}
          />
        </div>
        */}

        <div className="flex gap-1 sm:gap-2 items-center ms-auto">
          {leftActions}
          <AgentToolsList onOpen={onOpenToolsMenu} {...toolsMenuState} />
          {modern ? <Button type="button" variant="ghost" disabled={!models?.length}
            aria-label={model?.name ? `${t("common.model")}: ${model.name}` : t("common.model")}
            aria-expanded={settingsOpen} aria-controls={settingsId} onClick={event => openSettings(event.currentTarget)}
            className="max-w-[140px] gap-1 rounded-full font-normal motion-reduce:transition-none">
            <span className="truncate text-sm text-muted-foreground">{model?.name ?? ""}</span><ChevronDown aria-hidden="true" className="size-4" />
          </Button> : <DropdownMenu defaultOpen={openChange} onOpenChange={setOpenChange}>
            <DropdownMenuTrigger asChild disabled={!models?.length}>
              <Button
                type="button"
                variant="ghost"
                aria-label={
                  model?.name
                    ? `${t("common.model")}: ${model.name}`
                    : t("common.model")
                }
                title={model?.name}
                className={cn(
                  "h-auto gap-0 cursor-pointer duration-150 rounded-[35px] py-2 ps-3 pe-2 font-normal",
                  {
                    "bg-secondary text-[var(--color-100)] transition-all duration-200":
                      openChange,
                  },
                )}
              >
                <span className="max-w-[72px] sm:max-w-[100px] truncate text-muted-foreground text-xs sm:text-sm">
                  {model?.name ?? ""}
                </span>
                <span aria-hidden="true" className="flex items-center">
                  <ChevronDown
                    className="text-[12px] text-muted-foreground"
                    strokeWidth={1.75}
                  />
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[min(calc(100vw-1rem),224px)] sm:w-56 bg-[var(--card-background)] border-[1px] border-solid border-ring"
              align="end"
              side="bottom"
              sideOffset={6}
              collisionPadding={8}
            >
              <DropdownMenuLabel className="text-left rtl:text-right text-accent-foreground font-bold text-[10px] opacity-55">
                {t("common.model")}
              </DropdownMenuLabel>
              <DropdownMenuGroup>
                <ModelMenuSub
                  type="base"
                  models={models}
                  onModelChange={onModelChange}
                  selectedModel={model}
                  defaultModels={defaultModels}
                />
              </DropdownMenuGroup>

              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <SliderWithLabel
                  label={t("playground.temperature")}
                  min={0}
                  max={1}
                  step={0.01}
                  value={temperature}
                  onChange={onTemperatureChange}
                />
                <SliderWithLabel
                  label={t("playground.maximumTokens")}
                  min={0}
                  max={model?.max_tokens || 4096}
                  step={Math.max(
                    1,
                    Math.floor((model?.max_tokens || 4096) / 100),
                  )}
                  value={maxTokens}
                  onChange={onMaxTokensChange}
                />
              </DropdownMenuGroup>
              <DropdownMenuLabel className="text-left rtl:text-right text-accent-foreground font-bold text-[10px] opacity-55">
                {t("playground.language")}
              </DropdownMenuLabel>
              <DropdownMenuGroup>
                <LanguageMenuSub
                  language={language}
                  onLanguageChange={onLanguageChange}
                />
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>}
        </div>
      </div>
    </div>
  );
};

/** Same controlled bounds/callback semantics as SliderWithLabel, in Figma's compact row. */
function RunSettingsSlider({ label, min, max, step, value, onChange }: {
  label: string; min: number; max: number; step: number; value: number; onChange: (value: number) => void;
}) {
  const id = useId();
  const { i18n } = useTranslation();
  return <div className="flex flex-col gap-1" dir={i18n.dir()}>
    <span id={id} className="text-xs font-medium leading-[18px] text-foreground/70">{label}</span>
    <div className="flex items-center gap-2">
      <Slider.Root aria-labelledby={id} dir={i18n.dir()} min={min} max={max} step={step}
        value={[Math.min(max, Math.max(min, value))]} onValueChange={([next]) => { if (next !== undefined) onChange(next); }}
        className="relative flex h-6 min-w-0 flex-1 touch-none select-none items-center">
        <Slider.Track className="relative h-1 grow rounded-full bg-accent">
          <Slider.Range className="absolute h-full rounded-full bg-primary" />
        </Slider.Track>
        <Slider.Thumb aria-labelledby={id} className="block size-3 rounded-full border border-border bg-foreground outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background" />
      </Slider.Root>
      <output aria-labelledby={id} className="flex h-6 w-14 shrink-0 items-center rounded border border-border bg-input px-1.5 text-xs text-foreground">{value}</output>
    </div>
  </div>;
}

const ModelMenuSub = ({
  models,
  selectedModel,
  onModelChange,
  type,
  defaultModels,
  panel = false,
}: {
  models?: Array<Model>;
  selectedModel?: Model;
  onModelChange: (modelId: string) => void;
  type: string;
  defaultModels?: IDefaultModel[];
  panel?: boolean;
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const defaultModelId = defaultModels?.find((m) =>
    type === "base" ? m.mode === "base" : m.mode === "planner",
  )?.llm_config.id;

  const filteredModels = useMemo(() => {
    if (!models) return [];
    return models.filter((model) => {
      const isSupportedType = model.is_chat_model || model.is_multimodal;

      const isLegacyType =
        !model.is_chat_model &&
        !model.is_multimodal &&
        !model.is_default &&
        !model.is_embedding_model &&
        !model.is_image_generation_model &&
        !model.is_shared &&
        !model.is_stt_model &&
        !model.is_tts_model;

      return isSupportedType || isLegacyType;
    });
  }, [models]);

  const searchedModels = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase();
    if (!query) return filteredModels;
    return filteredModels.filter((model) =>
      model.name.toLocaleLowerCase().includes(query),
    );
  }, [filteredModels, searchQuery]);

  const Content = panel ? DropdownMenuContent : DropdownMenuSubContent;
  const content = <Content
          className="w-[min(calc(100vw-1rem),306px)] border-[1px] border-solid border-ring bg-[var(--card-background)]"
          onCloseAutoFocus={() => setSearchQuery("")}
        >
          <div
            className="relative mb-1 px-1"
            onKeyDown={(event) => event.stopPropagation()}
          >
            <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t("playground.searchPlaceholder")}
              aria-label={t("playground.searchPlaceholder")}
              className="h-9 w-full rounded-md border border-border bg-transparent ps-9 pe-3 text-sm text-foreground outline-hidden placeholder:text-muted-foreground focus:border-primary"
            />
          </div>
          <div className="max-h-[320px] overflow-y-auto overscroll-contain pe-1 [scrollbar-color:var(--primary)_transparent]">
            {searchedModels.map((model) => (
              <DropdownMenuItem
                key={type + model.id}
                onClick={() => onModelChange(model.id)}
                className="flex min-w-0 items-center justify-between gap-2 rounded-[35px] border border-transparent outline-hidden data-[highlighted]:border-foreground data-[highlighted]:bg-accent focus:border-foreground focus:bg-accent focus:outline-hidden focus-visible:outline-hidden focus-visible:ring-0"
              >
                <span className="min-w-0 flex-1 truncate">{model.name}</span>
                {model.id === defaultModelId && (
                  <span className="bg-primary text-primary-foreground text-[10px] px-2 py-0.5 rounded-full font-medium">
                    {t("playground.default")}
                  </span>
                )}
              </DropdownMenuItem>
            ))}
            {searchedModels.length === 0 && (
              <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                {t("usageAndCost.llmConfigs.noModelsFound")}
              </p>
            )}
          </div>
        </Content>;
  return panel ? content : (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>{selectedModel?.name ?? ""}</DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        {content}
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
};

const LanguageMenuSub = ({
  language,
  onLanguageChange,
}: {
  language: string;
  onLanguageChange: (language: string) => void;
}) => {
  const { t } = useTranslation();
  const languages = [
    { value: "auto", label: t("playground.auto") },
    { value: "en", label: t("playground.english") },
    { value: "ar", label: t("playground.arabic") },
  ];
  const selectedLanguage =
    languages.find((lang) => lang.value === language) || languages[0];
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>{selectedLanguage.label}</DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className="border-[1px] border-solid border-ring bg-[var(--card-background)]">
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang.value}
              onClick={() => onLanguageChange(lang.value)}
            >
              {lang.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
};
